import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BlogArticle from './components/BlogArticle';
import BlogCard from './components/BlogCard';
import { blogPosts } from '@/mocks/blogPosts';
import { useTranslation } from 'react-i18next';
import useSEO from '@/hooks/useSEO';

const BlogArticlePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { i18n } = useTranslation();
  const isEs = i18n.resolvedLanguage === 'es';

  const post = blogPosts.find(p => p.slug === slug);
  const related = blogPosts.filter(p => p.slug !== slug).slice(0, 3);

  const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com';

  useSEO({
    title: post
      ? `${isEs ? post.titleEs : post.title} | Quinolux Glass Las Vegas`
      : 'Article Not Found | Quinolux Glass & Mirrors',
    description: post
      ? (isEs ? post.excerptEs : post.excerpt)
      : 'Article not found.',
    keywords: post ? post.tags.join(', ') : '',
    canonical: post ? `https://quinoluxglass.com/blog/${post.slug}` : undefined,
    jsonLd: post
      ? {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: isEs ? post.titleEs : post.title,
          description: isEs ? post.excerptEs : post.excerpt,
          datePublished: post.date,
          dateModified: post.date,
          author: {
            '@type': 'Organization',
            name: 'Quinolux Glass & Mirrors',
            url: siteUrl,
          },
          publisher: {
            '@type': 'Organization',
            name: 'Quinolux Glass & Mirrors',
            url: siteUrl,
          },
          image: post.img,
          url: `${siteUrl}/blog/${post.slug}`,
          keywords: post.tags.join(', '),
          articleSection: post.category,
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
              { '@type': 'ListItem', position: 2, name: 'Blog', item: `${siteUrl}/blog` },
              { '@type': 'ListItem', position: 3, name: isEs ? post.titleEs : post.title, item: `${siteUrl}/blog/${post.slug}` },
            ],
          },
        }
      : undefined,
  });

  if (!post) {
    return (
      <main>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-20">
          <div className="text-center">
            <h1 className="text-3xl font-light text-black mb-4">Article Not Found</h1>
            <Link to="/blog" className="text-black underline cursor-pointer">Back to Blog</Link>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main>
      <Navbar />

      <section className="pt-32 pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <BlogArticle post={post} />
        </div>
      </section>

      {/* Related Posts */}
      <section className="py-16 bg-[#F8F8F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <h2 className="text-3xl font-light text-black mb-10" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {isEs ? 'Artículos Relacionados' : 'Related Articles'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {related.map(p => <BlogCard key={p.id} post={p} />)}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default BlogArticlePage;
